import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

type NewsletterModalProps = {
  open: boolean;
  title: string;
  subtitle: string;
  placeholder: string;
  joinLabel: string;
  maybeLaterLabel: string;
  trustNoSpam: string;
  trustUnsubscribe: string;
  trustPrivacy: string;
  onClose: () => void;
};

export const NewsletterModal: React.FC<NewsletterModalProps> = ({
  open,
  title,
  subtitle,
  placeholder,
  joinLabel,
  maybeLaterLabel,
  trustNoSpam,
  trustUnsubscribe,
  trustPrivacy,
  onClose,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-foreground/20 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full min-h-[100svh] flex items-center justify-center p-4">
        <div
          ref={contentRef}
          className="bg-background w-full max-w-md border border-border shadow-2xl rounded-2xl p-6 sm:p-8 max-h-[calc(100svh-2rem)] overflow-auto"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold mb-2">{title}</h3>
            <p className="text-muted-foreground text-sm">{subtitle}</p>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
            <input
              type="email"
              placeholder={placeholder}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-background text-sm transition-all"
            />
            <button
              type="submit"
              className="w-full py-3 bg-foreground text-background rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              {joinLabel}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex flex-col gap-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="text-primary">✓</span>
                <span>{trustNoSpam}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary">✓</span>
                <span>{trustUnsubscribe}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary">✓</span>
                <span>{trustPrivacy}</span>
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-full mt-4 text-center text-muted-foreground hover:text-foreground text-xs transition-colors"
          >
            {maybeLaterLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
